import type { TableActions } from '@/components/table/types';
import type {
  QueryActionHandlers,
  QueryEntity,
} from '@/sections/insights/queryBuilder/entities/types';
import { createRowActions } from '@/sections/vulnerabilities/config/actionOptions/rowActions';
import type { Risk } from '@/types';

import { buildRiskColumns } from './columns';
import { getRiskFields } from './fields';
import { getRiskSimpleFilters } from './simpleFilters';

/**
 * Build row actions for a risk in QueryBuilder results
 * Adapter: wraps createRowActions (used by main Vulnerabilities table) for QueryBuilder context
 */
function buildRiskRowActions(
  item: unknown,
  handlers: QueryActionHandlers
): TableActions {
  const risk = item as Risk;

  return createRowActions(
    risk,
    {
      onSeverityChange: (severity: string) => {
        if (handlers.updateRisk) {
          handlers
            .updateRisk({
              key: risk.key,
              name: risk.name,
              status: `${risk.status[0] ?? ''}${severity}`,
              comment: risk.comment || '',
            })
            .then(
              () => handlers.invalidate?.(),
              () => handlers.invalidate?.()
            );
        } else {
          handlers.showToast(`Severity change to ${severity} initiated`);
        }
      },
      onStatusChange: (status: string) => {
        if (handlers.updateRisk) {
          handlers
            .updateRisk({
              key: risk.key,
              name: risk.name,
              status: `${status}${risk.status[1] ?? ''}`,
              comment: risk.comment || '',
            })
            .then(
              () => handlers.invalidate?.(),
              () => handlers.invalidate?.()
            );
        } else {
          handlers.showToast(`Status change to ${status} initiated`);
        }
      },
      onDelete: () => {
        if (handlers.updateRisk) {
          handlers
            .updateRisk({
              key: risk.key,
              name: risk.name,
              status: `D${risk.status[1] ?? ''}`,
              comment: risk.comment || '',
            })
            .then(
              () => handlers.invalidate?.(),
              () => handlers.invalidate?.()
            );
        } else {
          handlers.showToast('Delete initiated');
        }
      },
    },
    handlers.isPraetorianUser,
    handlers.vulnerabilityMode ?? 'legacy'
  );
}

/**
 * Risk QueryEntity - bundles columns, fields, and row actions
 */
export const riskEntity: QueryEntity<unknown> = {
  primaryLabel: { actual: 'Risk', display: 'Vulnerability' },
  buildColumns:
    buildRiskColumns as () => import('@/components/table/types').Columns<unknown>,
  getFields: getRiskFields,
  getSimpleFilters: getRiskSimpleFilters,
  buildRowActions: buildRiskRowActions,
};
